import { create, el, awaitRedraw } from "@dhx/ts-common/dom";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { baseProps, getFormItemCss, getValidationMessage, isVerify } from "../helper";
import { Popup } from "@dhx/ts-popup";
import {
	ValidationStatus,
	ICheckboxConfig,
	ItemEvent,
	ICheckboxEventHandlersMap,
	ICheckbox,
	ICheckboxProps,
} from "../types";
import { Label } from "./helper/label";
import { isEmptyObj } from "@dhx/ts-common/core";

export class Checkbox extends Label implements ICheckbox {
	public config: ICheckboxConfig;
	public events: IEventSystem<ItemEvent, ICheckboxEventHandlersMap> = new EventSystem();

	protected _handlers: any;
	protected _helper: Popup;

	private _inGroup: boolean;
	private _isValid = true;
	private _propsItem = [
		"required",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
		"text",
	];
	private _props = [...baseProps, ...this._propsItem];

	constructor(container: HTMLElement | string, config = {}) {
		super(container, config);

		this._initView(config);
		this._initHandlers();

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	setProperties(propertyConfig: ICheckboxProps, silent = false): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			(!silent && !this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()]))
		)
			return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
			}
		}
		!silent && this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(): ICheckboxProps {
		const obj: ICheckboxProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		const { hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [this.getValue()])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [this.getValue()]);
	}
	hide(init?: boolean): void {
		const { hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [this.getValue(), init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [this.getValue(), init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	validate(silent = false): boolean {
		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [this.getValue()])) return;

		if (this.config.required || this.config.$required) {
			this._isValid = !!this.config.checked;
		}
		if (!silent) {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.events.fire(ItemEvent.afterValidate, [this.getValue(), this._isValid]);
		}

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	setValue(checked: boolean, silent = false): void {
		if (typeof checked === "undefined" || checked === this.config.checked) return;

		this.config.checked = !!checked;
		if (!silent) {
			this.events.fire(ItemEvent.change, [this.getValue()]);
			isVerify(this.config) && this.validate();
		}
	}
	getValue(): string | boolean {
		const { value, checked } = this.config;
		return value ? (checked ? value : "") : !!checked;
	}
	clear(silent?: boolean): void {
		if (!this.config.checked) return;
		this.config.checked = false;
		if (!silent) {
			this.events.fire(ItemEvent.change, [this.getValue()]);
		}
	}
	destructor(): void {
		this.events && this.events.clear();
		this._inGroup = this._propsItem = this._props = this._isValid = this._uid = this.events = null;
		super._destructor();
		this.unmount();
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.input.el.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.input.el.blur();
		});
	}
	isChecked(): boolean {
		return !!this.config.checked;
	}
	private _initView(config: ICheckboxConfig) {
		this._inGroup = this.config.$group;
		if (!this._inGroup) {
			this.config = {
				type: config.type,
				id: config.id,
				name: config.name,
				disabled: false,
				required: false,
				label: "",
				labelWidth: "",
				labelPosition: "top",
				hiddenLabel: false,
				helpMessage: "",
				preMessage: "",
				successMessage: "",
				errorMessage: "",
				width: "content",
				height: "content",
			};
			for (const key in config) {
				if (key !== "id" && key !== "type" && key !== "name") {
					this.config[key] = config[key];
				}
			}
			if (this.config.helpMessage) {
				this._helper = new Popup({
					css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light",
				});
				this._helper.attachHTML(this.config.helpMessage);
			}
			this._handlers = {
				showHelper: e => {
					e.preventDefault();
					e.stopPropagation();
					this._helper.show(e.target, {
						mode: this.config.labelPosition === "left" ? "bottom" : "right",
					});
				},
				hideHelper: e => {
					e.preventDefault();
					e.stopPropagation();
					this._helper.hide();
				},
				onchange: (e: Event) => {
					this.config.checked = (e.target as HTMLInputElement).checked;
					this.events.fire(ItemEvent.change, [this.getValue()]);
					isVerify(this.config) && this.validate();
				},
				onfocus: () => this.events.fire(ItemEvent.focus, [this.getValue()]),
				onblur: () => this.events?.fire(ItemEvent.blur, [this.getValue()]),
				onkeydown: (event: KeyboardEvent) => {
					this.events.fire(ItemEvent.keydown, [event]);
				},
			};
		} else {
			this.config = {
				type: config.type,
				id: config.id,
				text: "",
				width: "content",
				height: "content",
				padding: 0,
			};
			for (const key in config) {
				if (key !== "id" && key !== "type" && key !== "name") {
					this.config[key] = config[key];
				}
			}
			this._handlers = {
				onchange: (e: Event) => {
					this.config.checked = (e.target as HTMLInputElement).checked;
					this.events.fire(ItemEvent.change, [this.getValue()]);
					isVerify(this.config) && this.validate();
				},
				onfocus: () => this.events.fire(ItemEvent.focus, [this.getValue(), this.config.id]),
				onblur: () => this.events?.fire(ItemEvent.blur, [this.getValue(), this.config.id]),
				onkeydown: (event: KeyboardEvent) => {
					this.events.fire(ItemEvent.keydown, [event, this.config.id]);
				},
			};
		}
		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
	}
	protected _initHandlers() {
		this.events.on(ItemEvent.change, () => this.paint());
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
	}
	protected _draw() {
		const {
			id,
			value,
			checked,
			disabled,
			name,
			required,
			$required,
			text,
			label,
			labelWidth,
			helpMessage,
		} = this.config;

		return el(
			"label.dhx_checkbox.dhx_form-group.dhx_form-group--checkbox",
			{
				class: getFormItemCss(this.config, isVerify(this.config) || !!$required),
			},
			!this._inGroup
				? [
						label || labelWidth || helpMessage || required ? this._drawLabel() : null,
						el(".dhx_checkbox__container", [
							el(".dhx_checkbox__holder", [
								el("input.dhx_checkbox__input", {
									type: "checkbox",
									"data-dhx-id": name || id,
									id,
									value,
									name,
									disabled,
									checked,
									onchange: this._handlers.onchange,
									onfocus: this._handlers.onfocus,
									onblur: this._handlers.onblur,
									onkeydown: this._handlers.onkeydown,
									required,
									_ref: "input",
									"aria-label": label || `checkbox ${text || ""}`,
									"aria-describedby": helpMessage
										? `dhx_label__help_${id || this._uid}`
										: null,
								}),
								el("span.dhx_checkbox__visual-input"),
								el("span.dhx_text", [text]),
							]),
							required &&
								getValidationMessage(this.config) &&
								el("span.dhx_input__caption", getValidationMessage(this.config)),
						]),
				  ]
				: [
						el(".dhx_checkbox__holder", [
							el("input.dhx_checkbox__input", {
								type: "checkbox",
								id,
								value: value || "",
								name: name || "",
								disabled,
								checked,
								onchange: this._handlers.onchange,
								onfocus: this._handlers.onfocus,
								onblur: this._handlers.onblur,
								onkeydown: this._handlers.onkeydown,
								required,
								_ref: "input",
							}),
							el("span.dhx_checkbox__visual-input"),
							el("span.dhx_text", [text]),
						]),
				  ]
		);
	}
}
